// ABOUTME: Renders applicant stage actions and persists stage selections.
// ABOUTME: Reconciles the applicant view with the saved stage state.
import React from "react";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import ButtonGroup from "@material-ui/core/ButtonGroup";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import Grow from "@material-ui/core/Grow";
import Paper from "@material-ui/core/Paper";
import Popper from "@material-ui/core/Popper";
import MenuItem from "@material-ui/core/MenuItem";
import MenuList from "@material-ui/core/MenuList";
import {
    Chip,
    Divider,
    Icon,
    List,
    ListItem,
    ListItemIcon,
    ListItemSecondaryAction,
    ListItemText,
    ListSubheader,
    Menu,
    Switch,
    Typography
} from "@material-ui/core";
import {
    IJobViewStages,
    IStagesOnlyTotal
} from "Pages/Job/JobView/useJobContext/JobViewTypes";
import {
    getGroupFromStage,
    pipelineGroupType
} from "Pages/Pipeline/components/helperPipeline";
import { getColorFromPipelineGroupType } from "Pages/Job/JobView/JobKanban/JobKanbanColumn";
import { useApiCache } from "customHooks/useApi";
import { useAuthState } from "_ReactContext/AuthContext";
import { getTotalStage } from "Pages/Job/JobView/JobKanban/helper";
import { useQueryClient } from "react-query";
import useShowMessageFullControl from "customHooks/useShowMessageFullControl";
import ChangeStageButtonDisqualifyReasonModal from "./ChangeStageButtonDisqualifyReasonModal";
import useGlobalValues from "_ReactContext/GlobalValues";
import { Forward } from "@material-ui/icons";
import TooltipCompleo from "Components/Tooltip";
import { createStyles, Theme, makeStyles } from "@material-ui/core/styles";
import ButtonIconTooltipCompleo from "Components/ButtonIconTooltipCompleo";
import JobKanbanApplicantChangeStageModal from "Pages/Job/JobView/JobKanban/JobKanbanApplicant/JobKanbanApplicantActions/JobKanbanApplicantChangeStageModal";
import useJobChangeStageFunctionParam from "Pages/Job/JobView/JobKanban/JobKanbanApplicant/JobKanbanApplicantActions/useJobChangeStageFunctionParam";
import {
    getApplicantStageChangeMessageKey,
    submitApplicantStageChange
} from "Pages/Job/applicantStageChange";

interface IOptions {
    id: string;
    label: string;
    isGroup: boolean;
    icon?: string;
    groupColor?: string;
    groupType?: string;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        chipCard: {
            marginRight: theme.spacing(0.5),
            marginLeft: 8,
            borderRadius: 6
        },
        chipNoMargin: {
            margin: 0,
            borderRadius: 6
        }
    })
);

const useGetOptions = (stages: IJobViewStages[], language: string) => {
    const { company } = useAuthState();
    const [options, setOptions] = React.useState<IOptions[]>([]);
    const [getPipelinePatterns] = useApiCache(
        "/pipeline/getpipelinepatterns",
        "post",
        { companyId: company.companyId },
        false,
        1000 * 60 * 10
    );
    const data = getPipelinePatterns.response.data || {};
    // const typesOfStage = data?.typesOfStage?.items || []
    const listTypesOfStage = data?.typesOfStage?.items || [];

    const optionsReturn: IOptions[] = [];
    let groupTypeGlobal: pipelineGroupType | null = null;
    if (listTypesOfStage.length) {
        for (const stage of stages) {
            const groupType = getGroupFromStage(stage);
            const colorGroup = getColorFromPipelineGroupType(groupType);
            const typeOfStage = listTypesOfStage.filter(
                (item: any) => item.value === stage.type
            )[0];

            if (groupType !== groupTypeGlobal) {
                const label = (typeOfStage || {})[`label-${language}`];
                optionsReturn.push({
                    id: groupType || "",
                    isGroup: true,
                    label: label,
                    groupColor: colorGroup.mainColor,
                    groupType: groupType || "",
                    icon: typeOfStage?.icon
                });
                groupTypeGlobal = groupType;
            }

            optionsReturn.push({
                id: stage.id,
                isGroup: false,
                label: stage.name,
                groupColor: colorGroup.mainColor,
                groupType: groupType || "",
                icon: typeOfStage?.icon
            });
        }
    }
    React.useEffect(() => {
        if (optionsReturn.length && !options.length) {
            setOptions(optionsReturn);
        }
    }, [optionsReturn]);
    return { options };
};

export type ChangeStageButtonDisqualifyReasonModalFnToRunType = (
    disqualifyReason?: Compleo.IObject | undefined,
    comment?: string
) => Promise<void>;
interface IModalDisqualifyReasonParams {
    open: boolean;
    fnToRun?: ChangeStageButtonDisqualifyReasonModalFnToRunType;
}
const globalValueMoveToBeginOfList = "changeStageMoveBeginList";
const globalValueChangeStageAddComments = "changeStageAddComments";

interface IChangeStageButton {
    currentStage: IJobViewStages;
    stages: IJobViewStages[];
    applicantData: Compleo.IObject;
    jobData: Compleo.IObject;
    language: string;
    listKeyToInvalidate?: string[];
    fnBeforeUpdate?: (
        applicantId: string
    ) => (((successful?: boolean) => boolean) & { finish?: () => void }) | null;
    fnAfterUpdate?: any;
    stagesOnlyTotal?: IStagesOnlyTotal[];
    currentIndex: number;
    outsideKanban?: boolean;
    showAsTag?: boolean;
    showOnlyAsIcon?: boolean;
    classIcon?: string;
    fontSize?: number;
    t: any;
    readonly: boolean;
    buttonBorder?: boolean;
}
const ChangeStageButton = (props: IChangeStageButton) => {
    const {
        stages,
        currentStage,
        applicantData,
        jobData,
        language,
        listKeyToInvalidate = [],
        fnBeforeUpdate,
        fnAfterUpdate,
        stagesOnlyTotal = [],
        currentIndex,
        outsideKanban = false,
        showAsTag = false,
        showOnlyAsIcon = false,
        fontSize,
        classIcon,
        t,
        readonly,
        buttonBorder = false
    } = props;
    const classes = useStyles();
    const { company } = useAuthState();
    const [globalValues, dispatchGlobalValues] = useGlobalValues();
    const [isBeginList, setIsBeginList] = React.useState(
        globalValues.userdb[globalValueMoveToBeginOfList]?.beginList === true
    );
    const [addCommentsList, setAddCommentsList] = React.useState(
        globalValues.userdb[globalValueChangeStageAddComments]?.add === true
    );
    const [changeStageModalOpen, setChangeStageModalOpen] = React.useState<{
        open: boolean;
        defaultNewStage?: IJobViewStages | null;
    }>({
        open: false,
        defaultNewStage: null
    });
    // const jobModalFN = useJobChangeStageFunctionParam();

    const changeMoveListValue = () => {
        const newValue = !isBeginList;
        setIsBeginList(newValue);
        dispatchGlobalValues.addOrEditGlobalValue({
            name: globalValueMoveToBeginOfList,
            type: "userdb",
            value: { beginList: newValue }
        });
    };

    const changeAddCommentsListValue = () => {
        const newValue = !addCommentsList;
        setAddCommentsList(newValue);
        dispatchGlobalValues.addOrEditGlobalValue({
            name: globalValueChangeStageAddComments,
            type: "userdb",
            value: { add: newValue }
        });
    };

    const queryClient = useQueryClient();
    const showMessageFullControl = useShowMessageFullControl();
    const [modalDisqualifyParams, setModalDisqualifyParams] = React.useState<
        IModalDisqualifyReasonParams
    >({ open: false });

    const [open, setOpen] = React.useState(false);
    const anchorRef = React.useRef<HTMLButtonElement>(null);

    const localOptions = useGetOptions(stages, language);

    const saveValues = async (newStage: IOptions) => {
        const fnToRun = async (
            disqualifyReason?: Compleo.IObject,
            comment?: string
        ) => {
            showMessageFullControl.openMessage({
                message: t("COMPLEOGENERAL_MessageUpdating"),
                variant: "info",
                showLinearProgress: true
            });
            const applicantId = applicantData.pk.split(":")[1];
            const addToEndOfList = !isBeginList;
            // setSelectedIndex(index);
            const dataPost: Compleo.IObject = {
                companyId: company.companyId,
                jobId: jobData.JobId,
                applicantId: applicantId,
                stageId: newStage.id,
                addToEndOfList: addToEndOfList,
                oldStageId: currentStage?.id,
                priorityObj: { orderData: [] },
                newIndex: 0
            };
            if (comment) {
                dataPost.comment = comment;
            }
            if (disqualifyReason) {
                dataPost.disqualifyReason = disqualifyReason;
                dataPost.disqualifyApplicants = true;
            }

            const stageChangeShouldApply = fnBeforeUpdate?.(applicantData.pk);
            if (fnBeforeUpdate && !stageChangeShouldApply) {
                showMessageFullControl.openMessage({
                    message: t("ApplicantStageChangeInProgress"),
                    autoHideDuration: 5000,
                    variant: "warning"
                });
                return;
            }
            const shouldApply = stageChangeShouldApply || (() => true);
            try {
                let dataAPI;
                try {
                    dataAPI = await submitApplicantStageChange(dataPost);
                } catch (error) {
                    shouldApply(false);
                    throw error;
                }

                if ([200, 202].includes(dataAPI.status)) {
                    if (dataAPI.data?.requestStatus === "superseded") {
                        shouldApply(false);
                    } else if (fnAfterUpdate) {
                        const destinationIndex = addToEndOfList
                            ? getTotalStage(stagesOnlyTotal, [newStage.id])
                            : 0;
                        await fnAfterUpdate({
                            outsideKanban,
                            destinationIndex,
                            applicantId: applicantData.pk,
                            stageData: currentStage,
                            currentIndex,
                            newStageId: newStage.id,
                            requestId: dataAPI.data?.requestId,
                            stageChangeShouldApply: shouldApply
                        });
                    }

                    if (listKeyToInvalidate.length) {
                        for (const keyName of listKeyToInvalidate) {
                            queryClient.invalidateQueries(keyName);
                        }
                    }
                } else {
                    shouldApply(false);
                }
                // showMessageFullControl.closeMessage();
                const messageKey = getApplicantStageChangeMessageKey(dataAPI);
                showMessageFullControl.openMessage(
                    messageKey
                        ? {
                              message: t(messageKey),
                              autoHideDuration: 5000,
                              variant:
                                  dataAPI.data?.requestStatus === "manual_review"
                                      ? "error"
                                      : "warning"
                          }
                        : {
                              message: t("COMPLEOGENERAL_MessageUpdated"),
                              autoHideDuration: 2000,
                              variant: "success"
                          }
                );
            } finally {
                stageChangeShouldApply?.finish?.();
            }
        };
        return fnToRun;
    };

    const handleMenuItemClick = async (
        event: React.MouseEvent<HTMLLIElement, MouseEvent>,
        index: number
    ) => {
        setOpen(false);
        const newStage = localOptions?.options[index];
        const newStageFullData = stages.filter(
            (stage: IJobViewStages) => stage.id === newStage.id
        )[0];
        const fnToExecute = await saveValues(newStage);
        if (newStage.groupType === "rejected") {
            // setModalDisqualifyParams({ open: true, fnToRun: fnToExecute });
            setChangeStageModalOpen({
                open: true,
                defaultNewStage: newStageFullData
            });
        } else if (addCommentsList) {
            setChangeStageModalOpen({
                open: true,
                defaultNewStage: newStageFullData
            });
        } else {
            await fnToExecute();
        }
    };

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = (event: React.MouseEvent<Document, MouseEvent>) => {
        if (
            anchorRef.current &&
            anchorRef.current.contains(event.target as HTMLElement)
        ) {
            return;
        }

        setOpen(false);
    };
    if (!currentStage) return null;
    const currentStageData = stages.filter(
        (stage: IJobViewStages) => stage.id === currentStage.id
    )[0];

    const seletectedOption = localOptions.options.filter(
        (item) => item.id === currentStage?.id
    )[0];

    if (!seletectedOption) {
        return null;
    }
    if (!readonly) {
        return (
            <>
                {showOnlyAsIcon ? (
                    <ButtonIconTooltipCompleo
                        label={t("DialogChangeStage_title")}
                        onClick={handleToggle}
                        className={classIcon}
                    >
                        <Forward innerRef={anchorRef} />
                    </ButtonIconTooltipCompleo>
                ) : (
                    <Button
                        variant={
                            buttonBorder
                                ? "outlined"
                                : showAsTag
                                ? "contained"
                                : "text"
                        }
                        startIcon={!showAsTag && <Forward />}
                        color="inherit"
                        size="small"
                        onClick={handleToggle}
                        ref={anchorRef}
                        endIcon={<ArrowDropDownIcon />}
                        aria-controls={open ? "split-button-menu" : undefined}
                        aria-expanded={open ? "true" : undefined}
                        aria-label="select merge strategy"
                        aria-haspopup="menu"
                        style={{
                            backgroundColor: showAsTag
                                ? seletectedOption?.groupColor
                                : undefined,
                            color: showAsTag ? "white" : undefined,
                            textTransform: "none"
                        }}
                    >
                        <Typography
                            noWrap
                            style={{
                                margin: 0,
                                padding: 0,
                                fontSize: fontSize
                            }}
                        >
                            {seletectedOption?.label}
                        </Typography>
                    </Button>
                )}
                <Menu
                    anchorEl={anchorRef.current}
                    keepMounted
                    open={open}
                    onClose={handleClose}
                    PaperProps={{
                        style: {
                            width: 370
                        }
                    }}
                >
                    {localOptions.options.map((option, index) => (
                        <RenderMenuItem
                            key={option.id}
                            handleMenuItemClick={handleMenuItemClick}
                            index={index}
                            option={option}
                            seletectedOption={seletectedOption}
                        />
                    ))}
                    <Divider />
                    <List
                        subheader={
                            <ListSubheader>
                                {t(
                                    "CHANGESTAGEBUTTON_moveToBeginOfListSettins"
                                )}
                            </ListSubheader>
                        }
                    >
                        <ListItem>
                            <ListItemIcon>
                                <TooltipCompleo
                                    title={t("COMPLEOGENERAL_HELP")}
                                    detail={t(
                                        "CHANGESTAGEBUTTON_moveToBeginOfListHelp"
                                    )}
                                />
                            </ListItemIcon>
                            <ListItemText
                                id="changeEndOfList"
                                primary={t(
                                    "CHANGESTAGEBUTTON_moveToBeginOfList"
                                )}
                            />
                            <ListItemSecondaryAction>
                                <Switch
                                    edge="end"
                                    onChange={changeMoveListValue}
                                    checked={isBeginList}
                                    inputProps={{
                                        "aria-labelledby": "changeEndOfList"
                                    }}
                                />
                            </ListItemSecondaryAction>
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <TooltipCompleo
                                    title={t("COMPLEOGENERAL_HELP")}
                                    detail={t(
                                        "CHANGESTAGEBUTTON_addCommentsHelp"
                                    )}
                                />
                            </ListItemIcon>
                            <ListItemText
                                id="changeAddComments"
                                primary={t("CHANGESTAGEBUTTON_addComments")}
                            />
                            <ListItemSecondaryAction>
                                <Switch
                                    edge="end"
                                    onChange={changeAddCommentsListValue}
                                    checked={addCommentsList}
                                    inputProps={{
                                        "aria-labelledby": "changeAddComments"
                                    }}
                                />
                            </ListItemSecondaryAction>
                        </ListItem>
                    </List>
                </Menu>
                {showMessageFullControl.MessageElement}
                <ChangeStageButtonDisqualifyReasonModal
                    open={modalDisqualifyParams.open}
                    onClose={() => setModalDisqualifyParams({ open: false })}
                    fnToRun={modalDisqualifyParams.fnToRun}
                />
                {changeStageModalOpen.open && (
                    <JobKanbanApplicantChangeStageModal
                        onClose={() => setChangeStageModalOpen({ open: false })}
                        open={changeStageModalOpen.open}
                        allStages={stages}
                        stageData={currentStageData}
                        defaultNewStage={changeStageModalOpen.defaultNewStage}
                        t={t}
                        ApplicantData={applicantData}
                        currentIndex={currentIndex}
                        stagesOnlyTotal={stagesOnlyTotal}
                        listKeyToInvalidate={listKeyToInvalidate}
                        changeStage={
                            changeStageModalOpen?.defaultNewStage?.type !==
                            "rejected"
                        }
                        outsideKanban={outsideKanban}
                        fnBeforeUpdate={fnBeforeUpdate}
                        fnAfterUpdate={fnAfterUpdate}
                    />
                )}
            </>
        );
    } else
        return (
            <Chip
                className={showAsTag ? classes.chipCard : classes.chipNoMargin}
                style={{
                    backgroundColor: showAsTag
                        ? seletectedOption?.groupColor
                        : "transparent",
                    color: "white",
                    fontSize: !showAsTag ? 16 : undefined
                }}
                size={showAsTag ? "small" : "medium"}
                label={seletectedOption?.label}
            />
        );
};

interface IRenderMenuItem {
    handleMenuItemClick: (
        event: React.MouseEvent<HTMLLIElement, MouseEvent>,
        index: number
    ) => void;
    index: number;
    seletectedOption: IOptions;
    option: IOptions;
}

const RenderMenuItem = (props: IRenderMenuItem) => {
    const { handleMenuItemClick, index, seletectedOption, option } = props;
    const currentOption = option;
    const isSelected = option.id === seletectedOption.id;
    const enableButton =
        currentOption.isGroup === true || isSelected ? undefined : true;
    const onClickFN =
        currentOption.isGroup === true || isSelected
            ? undefined
            : (event: any) => handleMenuItemClick(event, index);

    return (
        <>
            <MenuItem
                selected={isSelected}
                onClick={onClickFN}
                button={enableButton}
            >
                {!currentOption.isGroup && (
                    <ListItemIcon>
                        <Icon
                            fontSize="small"
                            style={{ color: currentOption.groupColor }}
                        >
                            {currentOption.icon}
                        </Icon>
                    </ListItemIcon>
                )}
                <ListItemText primary={currentOption.label} />
            </MenuItem>
            {currentOption.isGroup && <Divider />}
        </>
    );
};

export default ChangeStageButton;
